import React, { useEffect, useState, useCallback } from 'react';
import {
    TextField, Paper, Button, Typography, Card, CardContent,
    CircularProgress, Grid, Modal, IconButton,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    Autocomplete, InputAdornment, Chip, Box,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AddIcon from '@mui/icons-material/Add';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import axios from 'axios';
import apiService from '../../services/actions';
import { toast } from 'react-toastify';

const T = {
    primary: '#1976d2',
    primaryLight: '#e3f2fd',
    primaryMid: '#bbdefb',
    primaryDark: '#1565c0',
    secondary: '#9c27b0',
    success: '#2e7d32',
    successBg: '#e8f5e9',
    error: '#c62828',
    errorBg: '#ffebee',
    errorMid: '#ef9a9a',
    border: '#e0e0e0',
    rowHover: '#f0f7ff',
    rowSelected: '#e3f2fd',
    rowAlt: '#fafafa',
    textPrimary: '#212121',
    textSecondary: '#616161',
    sectionBg: '#f8fafc',
    disabled: '#bdbdbd',
};

const EMPTY_FORM = {
    fullName: '',
    email: '',
    phoneNumber: '',
    password: '',
    roleId: 5,
    clinicIds: [],
    createdBy: '',
};

// Status filter options
const STATUS_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
];

/**
 * Normalise phone number:
 * - Strip all non-digit characters.
 * - If the result is empty or has fewer than 10 digits, return it as-is
 *   (don't force a country code on a partial/incomplete number).
 * - If it already starts with '91' and has 12 digits, return as-is.
 * - Otherwise prepend '91'.
 */
const normalisePhone = (phone) => {
    const digits = (phone || '').replace(/\D/g, '');
    if (!digits || digits.length < 10) return digits; // too short — don't prefix
    if (digits.startsWith('91') && digits.length === 12) return digits; // already has country code
    return `91${digits}`;
};

function SectionLabel({ icon, label }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 0.75, borderBottom: `2px solid ${T.primaryMid}` }}>
            <Box sx={{ color: T.primary, display: 'flex', alignItems: 'center' }}>{icon}</Box>
            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.primary }}>
                {label}
            </Typography>
        </Box>
    );
}

function UserAvatar({ name }) {
    const initials = (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
    return (
        <Box sx={{
            width: 30, height: 30, borderRadius: '50%',
            background: `linear-gradient(135deg, ${T.primary} 0%, ${T.secondary} 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '11px', fontWeight: 700, flexShrink: 0,
        }}>
            {initials}
        </Box>
    );
}

function MobilabUserListNew() {
    const loginObjStr = localStorage.getItem('loginObj');
    const loginObj = JSON.parse(loginObjStr);
    const userCode = loginObj?.userSession?.userCode || null;

    const [loading, setLoading] = useState(false);
    const [tableList, setTableList] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(null);

    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const itemsPerPage = 10;

    const [filterEclinicCode, setFilterEclinicCode] = useState('');
    const [filterPhone, setFilterPhone] = useState('');
    const [filterStatus, setFilterStatus] = useState(''); // '' = all, 'true' = active, 'false' = inactive
    const [filterEclinicOptions, setFilterEclinicOptions] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [editId, setEditId] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [showPassword, setShowPassword] = useState(false);

    const [eclinicOptions, setEclinicOptions] = useState([]);
    const [selectedEclinics, setSelectedEclinics] = useState([]);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const getEclinicsData = useCallback(async (searchTerm) => {
        if (!searchTerm || searchTerm.length < 3) {
            setFilterEclinicOptions([]);
            return;
        }
        try {
            const authToken = localStorage.getItem('authToken');
            const response = await axios.get(
                `${process.env.REACT_APP_COMMON_URL}EclinicAddress/list?eclinicName=${searchTerm}`,
                { headers: { Authorization: `Bearer ${authToken}` } }
            );
            if (response?.data?.data) {
                setFilterEclinicOptions(response.data.data || []);
            } else {
                setFilterEclinicOptions([]);
            }
        } catch (error) {
            console.error("Error fetching eClinics:", error);
            setFilterEclinicOptions([]);
        }
    }, []);

    const getTableList = async (
        pageNo = 0,
        eclinicCode = filterEclinicCode,
        phoneNumber = filterPhone,
        status = filterStatus
    ) => {
        setLoading(true);
        try {
            const params = { page: pageNo, size: itemsPerPage };
            if (eclinicCode) params.eclinicCode = eclinicCode;
            if (phoneNumber) params.phoneNumber = phoneNumber;
            if (status !== '') params.status = status == false ? 0 : 1
            const res = await apiService.getMobilabUsers(params);
            const data = res?.data?.data || {};
            setTableList(data.content || []);
            setCurrentPage(data.page ?? pageNo);
            setTotalPages(data.totalPages ?? 1);
            setTotalElements(data.totalElements ?? 0);
        } catch {
            setTableList([]);
            setTotalPages(1);
            setTotalElements(0);
        }
        setLoading(false);
    };

    const fetchEclinicDropdown = async (q = '') => {
        try {
            const r = await apiService.getEclinicDropdownUser(q);
            const options = r?.data?.data || [];
            setEclinicOptions(options);
            return options;
        } catch {
            setEclinicOptions([]);
            return [];
        }
    };

    const handleFilterEclinicChange = (v) => {
        const c = v?.eclinicCode || '';
        setFilterEclinicCode(c);
        getTableList(0, c, filterPhone, filterStatus);
    };

    const handleFilterStatusChange = (e) => {
        const s = e.target.value;
        setFilterStatus(s);
        getTableList(0, filterEclinicCode, filterPhone, s);
    };

    const handleFilterSearch = () => getTableList(0, filterEclinicCode, filterPhone, filterStatus);
    const handleFilterReset = () => {
        setFilterEclinicCode('');
        setFilterPhone('');
        setFilterStatus('');
        setFilterEclinicOptions([]);
        getTableList(0, '', '', '');
    };

    const openAddModal = () => {
        setIsEdit(false);
        setEditId(null);
        setFormData({ ...EMPTY_FORM, createdBy: userCode });
        setSelectedEclinics([]);
        setEclinicOptions([]);
        setShowPassword(false);
        setModalOpen(true);
        fetchEclinicDropdown('');
    };

    const openEditModal = (row) => {
        setIsEdit(true);
        setEditId(row.id);
        setFormData({
            fullName: row.full_name || row.fullName || '',
            email: row.email || '',
            phoneNumber: row.phone_number || row.phoneNumber || '',
            password: '',
            roleId: row.role?.id || row.roleId || 5,
            clinicIds: row.clinicIds || [],
            createdBy: userCode,
        });
        setShowPassword(false);
        setModalOpen(true);
        fetchEclinicDropdown('').then((options) => {
            if (row.clinicIds?.length) {
                setSelectedEclinics(options.filter(o => row.clinicIds.includes(o.eclinicCode)));
            } else {
                setSelectedEclinics([]);
            }
        });
    };

    const closeModal = () => {
        setModalOpen(false);
        setFormData(EMPTY_FORM);
        setSelectedEclinics([]);
        setEclinicOptions([]);
        setShowPassword(false);
    };

    const handleEclinicChange = (_, newValue) => {
        setSelectedEclinics(newValue || []);
        setFormData(p => ({ ...p, clinicIds: (newValue || []).map(i => i.eclinicCode) }));
    };

    const validateForm = () => {
        // Only fullName is mandatory
        if (!formData.fullName) {
            toast.error('Please fill in full name.');
            return false;
        }
        // Clinic selection mandatory only when adding
        if (!isEdit && formData.clinicIds.length === 0) {
            toast.error('Please select at least one clinic.');
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) return;
        setLoading(true);
        try {
            let res;
            if (isEdit) {
                const payload = {
                    fullName: formData.fullName,
                    // Include email only if provided
                    ...(formData.email && { email: formData.email }),
                    // Include phone only if provided; normalise with smart prefix
                    ...(formData.phoneNumber && { phoneNumber: normalisePhone(formData.phoneNumber) }),
                    roleId: formData.roleId,
                };
                res = await apiService.updateMobilabUser(editId, payload);
            } else {
                const payload = {
                    clinicIds: formData.clinicIds,
                    fullName: formData.fullName,
                    // Include optional fields only when provided
                    ...(formData.email && { email: formData.email }),
                    ...(formData.phoneNumber && { phoneNumber: normalisePhone(formData.phoneNumber) }),
                    ...(formData.password && { password: formData.password }),
                    roleId: formData.roleId,
                    createdBy: userCode,
                };
                res = await apiService.createMobilabUser(payload);
            }
            if (res?.data?.responseCode === '200' || res?.status === 200 || res?.data?.status === true || res?.data?.message) {
                toast.success(isEdit ? 'User updated successfully.' : 'User created successfully.');
                closeModal();
                getTableList(isEdit ? currentPage : 0);
            } else {
                toast.error(res?.data?.reponseMessage || res?.data?.message || (isEdit ? 'Update failed.' : 'Save failed.'));
            }
        } catch {
            toast.error('Something went wrong.');
        }
        setLoading(false);
    };

    const openDeleteDialog = (row) => {
        // Guard: only active users can be deleted
        if (row.is_active === false) return;
        setUserToDelete(row);
        setDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setUserToDelete(null);
    };

    const handleDelete = async () => {
        if (!userToDelete) return;
        setLoading(true);
        try {
            const res = await apiService.deleteMobilabUser(userToDelete.id);
            const resData = res?.data;
            const isSuccess =
                res?.status === 200 ||
                resData?.responseCode === '200' ||
                resData?.status === true ||
                resData?.message?.toLowerCase().includes('success') ||
                resData?.reponseMessage?.toLowerCase().includes('success') ||
                resData?.data?.is_active === false;

            if (isSuccess) {
                toast.success('User deleted successfully.');
                closeDeleteDialog();
                getTableList(currentPage);
            } else {
                toast.error(resData?.reponseMessage || resData?.message || 'Delete failed.');
            }
        } catch {
            toast.error('Something went wrong.');
        }
        setLoading(false);
    };

    useEffect(() => {
        getTableList(0);
    }, []);

    const formatDate = (d) => {
        if (!d) return '-';
        try {
            return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch {
            return d;
        }
    };

    const pageNumbers = Array.from({ length: totalPages }, (_, i) => i);
    const visiblePages = pageNumbers.filter(p => p === 0 || p === totalPages - 1 || Math.abs(p - currentPage) <= 1);

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            height: 34, borderRadius: '7px', fontSize: '0.8125rem', bgcolor: '#fff',
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.primary },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.primary },
        },
        '& .MuiInputLabel-root': { fontSize: '0.8125rem', top: '-2px', '&.MuiInputLabel-shrink': { top: 0 } },
        '& .MuiInputLabel-root.Mui-focused': { color: T.primary },
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mt: 0, px: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, pb: 1, borderBottom: `1px solid ${T.border}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box sx={{
                        width: 32, height: 32, borderRadius: '8px',
                        background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 2px 8px ${T.primaryMid}`,
                    }}>
                        <PeopleOutlineIcon sx={{ color: '#fff', fontSize: 18 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '15px', color: T.textPrimary, lineHeight: 1.2 }}>Mobilab User List</Typography>
                        <Typography variant="caption" sx={{ color: T.textSecondary, fontSize: '11px' }}>Manage users and their clinic assignments</Typography>
                    </Box>
                </Box>
                <Button
                    variant="contained" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={openAddModal}
                    sx={{
                        borderRadius: '7px', textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', px: 2, height: 32,
                        background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
                        boxShadow: `0 2px 8px ${T.primaryMid}`,
                        '&:hover': { background: `linear-gradient(135deg, ${T.primaryDark} 0%, #0d47a1 100%)`, boxShadow: `0 4px 12px ${T.primaryMid}` },
                    }}
                >
                    Add User
                </Button>
            </Box>

            {loading && (
                <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.35)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress sx={{ color: T.primary }} />
                </Box>
            )}

            <Paper elevation={0} sx={{ p: 1.25, mb: 1, borderRadius: '10px', border: `1px solid ${T.border}`, background: T.sectionBg, position: 'sticky', top: 64, zIndex: 10 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                    <FilterListIcon sx={{ fontSize: 15, color: T.textSecondary }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: T.textSecondary, fontSize: '11px' }}>
                        Filters
                    </Typography>
                </Box>
                <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <Autocomplete
                            options={filterEclinicOptions}
                            getOptionLabel={(o) => o?.eclinicName ? `${o.eclinicName} (${o.eclinicCode})` : ''}
                            value={filterEclinicOptions.find(e => e.eclinicCode === filterEclinicCode) || null}
                            onInputChange={(_, v) => getEclinicsData(v)}
                            onChange={(_, v) => handleFilterEclinicChange(v)}
                            isOptionEqualToValue={(o, v) => o?.eclinicCode === v?.eclinicCode}
                            size="small"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Eclinic"
                                    variant="outlined"
                                    placeholder="Search eclinic…"
                                    sx={{
                                        ...fieldSx,
                                        '& .MuiOutlinedInput-root': {
                                            ...fieldSx['& .MuiOutlinedInput-root'],
                                            minHeight: 34, maxHeight: 34, py: '0px !important'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            fullWidth size="small" label="Phone Number" variant="outlined"
                            value={filterPhone}
                            onChange={(e) => setFilterPhone(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleFilterSearch()}
                            placeholder="Enter phone number…"
                            sx={fieldSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            select fullWidth size="small" label="Status" variant="outlined"
                            value={filterStatus}
                            onChange={handleFilterStatusChange}
                            sx={{ ...fieldSx, '& .MuiSelect-select': { py: '4px !important', display: 'flex', alignItems: 'center' } }}
                        >
                            {STATUS_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.8125rem' }}>{opt.label}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Box sx={{ display: 'flex', gap: 0.75, height: 34 }}>
                            <Button
                                variant="contained" size="small" startIcon={<SearchIcon sx={{ fontSize: 16 }} />} onClick={handleFilterSearch}
                                sx={{ borderRadius: '7px', textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', px: 2, height: '100%', bgcolor: T.primary, '&:hover': { bgcolor: T.primaryDark } }}
                            >
                                Search
                            </Button>
                            <Button
                                variant="outlined" size="small" startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />} onClick={handleFilterReset}
                                sx={{ borderRadius: '7px', textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', px: 2, height: '100%', borderColor: T.border, color: T.textSecondary, '&:hover': { borderColor: T.primary, color: T.primary, bgcolor: T.primaryLight } }}
                            >
                                Reset
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${T.border}`, overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)', minHeight: 350, overflow: 'auto' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{ background: `linear-gradient(90deg, ${T.primaryLight} 0%, #f8f9ff 100%)` }}>
                                {['Full Name', 'Phone Number', 'Eclinic Code', 'Status', 'Created', 'Actions'].map(col => (
                                    <TableCell key={col} sx={{ fontWeight: 700, fontSize: '12px', letterSpacing: '0.04em', textTransform: 'uppercase', color: T.primary, borderBottom: `2px solid ${T.primaryMid}`, py: 1.5, whiteSpace: 'nowrap', bgcolor: T.primaryLight }}>
                                        {col}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableList.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: T.textSecondary }}>
                                            <PeopleOutlineIcon sx={{ fontSize: 40, opacity: 0.25 }} />
                                            <Typography variant="body2">No users found</Typography>
                                            <Typography variant="caption">Try adjusting your filters or add a new user</Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tableList.map((row, index) => {
                                    const isActive = row.is_active !== false;
                                    return (
                                        <TableRow
                                            key={row.id}
                                            onClick={() => setSelectedIndex(index)}
                                            sx={{
                                                cursor: 'pointer',
                                                bgcolor: selectedIndex === index ? T.rowSelected : (index % 2 === 0 ? '#fff' : T.rowAlt),
                                                borderLeft: selectedIndex === index ? `3px solid ${T.primary}` : '3px solid transparent',
                                                transition: 'all 0.15s ease',
                                                '&:hover': { bgcolor: T.rowHover, borderLeft: `3px solid ${T.primaryMid}` },
                                                '& td': { borderBottom: `1px solid ${T.border}`, py: 1.25, fontSize: '13px' },
                                            }}
                                        >
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <UserAvatar name={row.full_name || row.fullName} />
                                                    <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                                                        {row.full_name || row.fullName || '-'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ color: T.textSecondary, fontFamily: 'monospace', fontSize: '12px' }}>
                                                {row.phone_number || row.phoneNumber || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {row.eclinicCode ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: T.primary, flexShrink: 0 }} />
                                                        <Typography sx={{ fontSize: '12px', fontWeight: 600, fontFamily: 'monospace', color: T.primary }}>
                                                            {row.eclinicCode}
                                                        </Typography>
                                                    </Box>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={isActive ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    sx={{
                                                        fontSize: '11px', fontWeight: 700, height: 22, borderRadius: '6px',
                                                        bgcolor: isActive ? T.successBg : T.errorBg,
                                                        color: isActive ? T.success : T.error,
                                                        border: `1px solid ${isActive ? '#a5d6a7' : T.errorMid}`,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ color: T.textSecondary, fontSize: '12px' }}>
                                                {formatDate(row.created)}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditModal(row); }}
                                                        sx={{ color: T.primary, '&:hover': { bgcolor: T.primaryLight } }}>
                                                        <EditIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                    {/* Delete only allowed for active users */}
                                                    <IconButton
                                                        size="small"
                                                        disabled={!isActive}
                                                        title={isActive ? 'Delete user' : 'Already inactive'}
                                                        onClick={(e) => { e.stopPropagation(); openDeleteDialog(row); }}
                                                        sx={{
                                                            color: isActive ? T.error : T.disabled,
                                                            '&:hover': isActive ? { bgcolor: T.errorBg } : {},
                                                        }}
                                                    >
                                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderTop: `1px solid ${T.border}`, bgcolor: T.sectionBg, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: T.textSecondary }}>
                        Showing <strong>{tableList.length}</strong> of <strong>{totalElements}</strong> users
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        <button onClick={() => getTableList(currentPage - 1)} disabled={currentPage === 0}
                            style={{ padding: '4px 10px', border: `1px solid ${T.border}`, borderRadius: '7px', background: '#fff', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, fontSize: '14px', fontWeight: 600, color: T.textPrimary }}>
                            ‹
                        </button>
                        {visiblePages.map((p, idx) => {
                            const prev = visiblePages[idx - 1];
                            const ellipsis = prev !== undefined && p - prev > 1;
                            return (
                                <React.Fragment key={p}>
                                    {ellipsis && <span style={{ color: T.textSecondary, padding: '0 2px' }}>…</span>}
                                    <button onClick={() => getTableList(p)}
                                        style={{ padding: '4px 10px', border: `1px solid ${currentPage === p ? T.primary : T.border}`, borderRadius: '7px', fontSize: '13px', fontWeight: currentPage === p ? 700 : 400, background: currentPage === p ? T.primary : '#fff', color: currentPage === p ? '#fff' : T.textPrimary, cursor: 'pointer', transition: 'all 0.15s' }}>
                                        {p + 1}
                                    </button>
                                </React.Fragment>
                            );
                        })}
                        <button onClick={() => getTableList(currentPage + 1)} disabled={currentPage >= totalPages - 1}
                            style={{ padding: '4px 10px', border: `1px solid ${T.border}`, borderRadius: '7px', background: '#fff', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, fontSize: '14px', fontWeight: 600, color: T.textPrimary }}>
                            ›
                        </button>
                    </Box>
                </Box>
            </Paper>

            <Modal open={modalOpen} sx={{ backdropFilter: 'blur(3px)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', minHeight: '100vh', pt: 5, pb: 5, overflowY: 'auto' }}>
                    <Card sx={{ width: '82vw', maxWidth: 820, borderRadius: '14px', boxShadow: '0 24px 60px rgba(0,0,0,0.18)', overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, background: `linear-gradient(90deg, ${T.primary} 0%, ${T.primaryDark} 100%)` }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <PeopleOutlineIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 22 }} />
                                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#fff', letterSpacing: '0.01em' }}>
                                    {isEdit ? 'Edit User' : 'Add New User'}
                                </Typography>
                            </Box>
                            <IconButton onClick={closeModal} size="small" sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' } }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>

                        <CardContent sx={{ p: 3 }}>
                            <Grid container spacing={2.5}>
                                <Grid item xs={12}>
                                    <SectionLabel icon={<PersonOutlineIcon sx={{ fontSize: 15 }} />} label="Personal Information" />
                                </Grid>

                                {/* Full Name — only mandatory field */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth label="Full Name *" variant="outlined" size="small"
                                        value={formData.fullName}
                                        onChange={(e) => setFormData(p => ({ ...p, fullName: e.target.value }))}
                                        sx={fieldSx}
                                    />
                                </Grid>

                                {/* Phone — optional */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth label="Phone Number" variant="outlined" size="small"
                                        value={formData.phoneNumber}
                                        onChange={(e) => setFormData(p => ({ ...p, phoneNumber: e.target.value }))}
                                        placeholder="e.g. 9876543210"
                                        helperText="Country code 91 added automatically for 10-digit numbers"
                                        sx={fieldSx}
                                    />
                                </Grid>

                                {/* Email — optional */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth label="Email" variant="outlined" size="small"
                                        value={formData.email}
                                        onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                                        sx={fieldSx}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ fontSize: 16, color: T.textSecondary }} /></InputAdornment>,
                                        }}
                                    />
                                </Grid>

                                {/* Password — optional, only shown when adding */}
                                {!isEdit && (
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth label="Password" variant="outlined" size="small"
                                            type={showPassword ? 'text' : 'password'}
                                            value={formData.password}
                                            onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                                            sx={fieldSx}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ fontSize: 16, color: T.textSecondary }} /></InputAdornment>,
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => setShowPassword(p => !p)} edge="end">
                                                            {showPassword ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <SectionLabel icon={<LocalHospitalOutlinedIcon sx={{ fontSize: 15 }} />} label="Clinic Assignment" />
                                </Grid>
                                <Grid item xs={12}>
                                    <Autocomplete
                                        multiple
                                        options={eclinicOptions}
                                        getOptionLabel={(o) => `${o.eclinicCode} – ${o.eclinicName}`}
                                        value={selectedEclinics}
                                        onChange={handleEclinicChange}
                                        onInputChange={(_, v) => fetchEclinicDropdown(v)}
                                        isOptionEqualToValue={(o, v) => o?.eclinicCode === v?.eclinicCode}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={isEdit ? 'Clinics' : 'Clinics *'}
                                                variant="outlined" size="small"
                                                placeholder="Search and select clinics"
                                                helperText={isEdit ? 'Update clinic assignments if needed' : 'Select one or more clinics for this user'}
                                                sx={fieldSx}
                                            />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    variant="outlined" label={option.eclinicCode}
                                                    {...getTagProps({ index })} size="small"
                                                    sx={{ fontSize: '11px', fontWeight: 600, bgcolor: T.primaryLight, borderColor: T.primaryMid, color: T.primary }}
                                                />
                                            ))
                                        }
                                    />
                                </Grid>
                                {formData.clinicIds.length > 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" sx={{ color: T.textSecondary }}>
                                            {formData.clinicIds.length} clinic{formData.clinicIds.length > 1 ? 's' : ''} selected: {formData.clinicIds.join(', ')}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 3, py: 2, borderTop: `1px solid ${T.border}`, bgcolor: T.sectionBg }}>
                            <Button
                                variant="outlined" onClick={closeModal}
                                sx={{ borderRadius: '9px', textTransform: 'none', fontWeight: 600, fontSize: '13px', px: 3, borderColor: T.border, color: T.textSecondary, '&:hover': { borderColor: T.primary, color: T.primary, bgcolor: T.primaryLight } }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained" onClick={handleSave} disabled={loading}
                                sx={{
                                    borderRadius: '9px', textTransform: 'none', fontWeight: 700, fontSize: '13px', px: 3.5, minWidth: 110,
                                    background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
                                    boxShadow: `0 2px 8px ${T.primaryMid}`,
                                    '&:hover': { background: `linear-gradient(135deg, ${T.primaryDark} 0%, #0d47a1 100%)` },
                                }}
                            >
                                {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : (isEdit ? 'Save Changes' : 'Add User')}
                            </Button>
                        </Box>
                    </Card>
                </Box>
            </Modal>

            <Dialog
                open={deleteDialogOpen} onClose={closeDeleteDialog}
                PaperProps={{ sx: { borderRadius: '14px', p: 0.5, maxWidth: 420 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 2.5, pb: 1 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: T.errorBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <WarningAmberRoundedIcon sx={{ color: T.error, fontSize: 20 }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '16px' }}>Delete User</Typography>
                </DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <DialogContentText sx={{ fontSize: '14px', color: T.textSecondary }}>
                        Are you sure you want to delete{' '}
                        <strong style={{ color: T.textPrimary }}>{userToDelete?.full_name || userToDelete?.fullName}</strong>?
                        This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeDeleteDialog}
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '13px', px: 2.5, color: T.textSecondary, border: `1px solid ${T.border}`, '&:hover': { bgcolor: T.sectionBg } }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} variant="contained"
                        sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '13px', px: 2.5, bgcolor: T.error, '&:hover': { bgcolor: '#b71c1c' } }}>
                        {loading ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default MobilabUserListNew;